import { PI_RADIANS_360, type Radians } from '../utils/angles';
import type { Point } from './point';

export interface Vector {
  readonly x: number;
  readonly y: number;
}

export interface PositionVector {
  point: Point;
  vector: Vector;
}

/**
 * "Type hack" to ensure nonzero vector.
 *
 * It is intentionally not possible to assign any {x, y} value
 * to avoid creating a zero vector under this type:
 * const mee: NonZeroVector = {x: 0, y: 0};
 *
 * Proper usage of this type is:
 * - check whether a vector is nonzero using type guard function isNonZero
 * - elevate with assertType(is[Non]Zero, v) or throw exception when the vector is zero (use where you don't expect zero vectors)
 * - type assertion, e.g. {x: 1, y: 1} as NonZeroVector where you are absolutely sure you are creating
 *   a non-zero vector (e.g. when creating unit vector under given angle).
 * - this type is also returned by functions there that surely don't create zero vectors, e.g.
 *   we can safely assume reverse(NonZeroVector) -> NonZeroVector.
 */
declare const __nonzero: unique symbol;
export interface NonZeroVector extends Vector {
  readonly [__nonzero]: never;
}

export interface ZeroVector {
  x: 0;
  y: 0;
}

// factories

export function fromAtoB(a: Point, b: Point): Vector {
  return {
    x: b.x - a.x,
    y: b.y - a.y,
  };
}

/**
 * Create a vector defined by given point
 *
 * The "fancy" type signature allows the return type to be NonZeroVector where Typescript is able
 * to determine that at least one coordinate is a non-zero literal.
 *
 * ```
 * declare const x: number, y: number;
 * const z = 5;
 * fromPoint(x, y); // Vector
 * fromPoint(x, 0); // Vector
 * fromPoint(0, 0); // Vector
 * fromPoint(x, 1); // NonZeroVector
 * fromPoint(0, 1); // NonZeroVector
 * fromPoint(x, z); // NonZeroVector (simple literal variables are inferred as particular number)
 * fromPoint(0, 3*5) // Vector (although it is obviously non-zero, typescript do not infer expressions to particular number)
 * ```
 */
export function fromCoords<TX extends number, TY extends number>(
  x: TX,
  y: TY,
): 0 extends TX ? (0 extends TY ? Vector : NonZeroVector) : NonZeroVector {
  // the return type is matched, however typescript is not able to understand the exclusion of zero properly

  return { x, y } as any;
}

export function unit(angle = 0): NonZeroVector {
  return {
    x: Math.cos(angle),
    y: Math.sin(angle),
  } as NonZeroVector;
}

export const zero: ZeroVector = { x: 0, y: 0 } as const;

// arithmetic functions

export function add(a: Vector, b: Vector): Vector {
  return {
    x: a.x + b.x,
    y: a.y + b.y,
  };
}

/**
 * Divide a vector and a scalar
 */
export function div(v: NonZeroVector, scalar: number): NonZeroVector;
export function div(v: Vector, scalar: number): Vector;
export function div(v: Vector, scalar: number): Vector {
  if (scalar === 0) {
    throw new RangeError('Division by zero');
  }
  return {
    x: v.x / scalar,
    y: v.y / scalar,
  };
}

/**
 * Get the dot-product of two vectors
 */
export function dot(a: Vector, b: Vector): number {
  return a.x * b.x + a.y * b.y;
}

/**
 * Multiply a vector and a scalar
 */
export function mult<TScalar extends number>(
  v: NonZeroVector,
  scalar: TScalar,
): 0 extends TScalar ? Vector : NonZeroVector;
export function mult(v: Vector, scalar: number): Vector;
export function mult(v: Vector, scalar: number): Vector {
  return {
    x: v.x * scalar,
    y: v.y * scalar,
  };
}

/**
 * Subtract the two vectors
 *
 * @return a - b
 */
export function sub(a: Vector, b: Vector): Vector {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
  };
}

// comparison

export function equals(a: Vector, b: Vector): boolean {
  return a.x === b.x && a.y === b.y;
}

// zero typing

export function isZero(v: Vector): v is ZeroVector {
  return v.x === 0 && v.y === 0;
}

export function isNonZero(v: Vector): v is NonZeroVector {
  return !isZero(v);
}

// geometry

export function getMagnitude({ x, y }: Vector): number {
  return Math.sqrt(x ** 2 + y ** 2);
}

export function getAngle(v: NonZeroVector): Radians {
  const length = getMagnitude(v);
  const angle = Math.acos(v.x / length);
  return v.y < 0 ? PI_RADIANS_360 - angle : angle;
}

/**
 * Normalize a vector (such that its magnitude is `1`)
 */
export function normalize(v: NonZeroVector): NonZeroVector {
  return div(v, getMagnitude(v));
}

/**
 * Return the perpendicular vector
 *
 * Perpendicularity option works only in Cartesian coordinate system with the upside down y-coordinates
 */

export function perp(
  v: NonZeroVector,
  counterClockwise?: boolean,
): NonZeroVector;
export function perp(v: Vector, counterClockwise: boolean): Vector;
export function perp(v: Vector, counterClockwise = false): Vector {
  const sign = counterClockwise ? -1 : 1;
  return {
    x: sign * -v.y,
    y: sign * v.x,
  };
}

/**
 * Get a vector of opposite direction and same size
 */
export function reverse(v: NonZeroVector): NonZeroVector;
export function reverse(v: Vector): Vector;
export function reverse(v: Vector): Vector {
  return mult(v, -1);
}

/**
 * Rotate the vector by `angle`
 */
export function rotateBy(
  { x, y }: NonZeroVector,
  angle: Radians,
): NonZeroVector;
export function rotateBy({ x, y }: Vector, angle: Radians): Vector;
export function rotateBy({ x, y }: Vector, angle: Radians): Vector {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
}

export function setMagnitude<TMagnitude extends number>(
  v: NonZeroVector,
  magnitude: TMagnitude,
): 0 extends TMagnitude ? Vector : NonZeroVector {
  return mult(v, (magnitude / getMagnitude(v)) as TMagnitude);
}

export const vector = {
  fromAtoB,
  fromCoords,
  unit,
  zero,

  add,
  div,
  dot,
  mult,
  sub,

  equals,

  isNonZero,
  isZero,

  getAngle,
  getMagnitude,
  normalize,
  perp,
  reverse,
  rotateBy,
  setMagnitude,
};
